export const CHALLENGE_PAGE_POSTHOG_VARIANTS = ['control', 'test', 'clarity'] as const;
export type ChallengePagePostHogVariant = (typeof CHALLENGE_PAGE_POSTHOG_VARIANTS)[number];

export const CHALLENGE_CLARITY_HOME_EXPERIMENT = {
  name: 'challenge_page_original_vs_clarity_home_2026_09_22',
  featureFlag: 'challenge-page-original-vs-clarity-home-2026-09-22',
  posthogExperimentId: 466563,
  status: 'draft',
  // control = Rob's original page (A), test = Martin's Clarity Home page (B),
  // form_first = short copy with the whole form on the first phone screen (C,
  // Rob 2026-09-23). The PostHog flag needs a form_first variant before C gets
  // traffic; until then a form_first value can only come from a preview.
  // Rob, 2026-10-02: this flag keeps deciding exactly as before whenever the
  // A/B/C flag below is not serving one of its own arms.
  posthogVariants: ['control', 'test', 'form_first'],
  primaryMetric: 'Confirmed challenge registration conversion',
} as const;

export type ChallengeClarityHomeVariant =
  (typeof CHALLENGE_CLARITY_HOME_EXPERIMENT.posthogVariants)[number];

/**
 * Rob's 2026-10-02 challenge page A/B/C test, on its own new PostHog flag.
 * A = control (today's page), B = form_first, C = form_first_ad_match (B with
 * the top Proven Google Ads heading and subheading, see challengeAdMatchCopy).
 *
 * The flag is created in PostHog as an unlaunched draft by the team, never by
 * this code. While it serves none of these arms (draft, off, missing, or an
 * unknown value), Martin's flag above decides exactly as it does today. Rob
 * ends experiment 466563 at the moment this one launches.
 */
export const CHALLENGE_ABC_EXPERIMENT = {
  name: 'challenge_page_abc_2026_10',
  featureFlag: 'challenge-page-abc-2026-10',
  status: 'draft',
  // trimmed and trimmed_headline (arms C and D, Rob approved 2026-10-20): A's
  // page with the repeats removed, and the same with a real-money headline
  // (challengeTrimmedCopy.ts). Like form_first_ad_match, they get traffic only
  // after the team adds them to the PostHog flag. Until the flag serves one,
  // no visitor is assigned to it. Keep Node's challengeExperimentAttribution
  // schema (botspot_node src/common/acquisitionAttribution.ts) in step, or a
  // signup from the arm loses its arm.
  posthogVariants: ['control', 'form_first', 'form_first_ad_match', 'trimmed', 'trimmed_headline'],
  primaryMetric: 'Confirmed challenge registration conversion',
} as const;

export type ChallengeAbcVariant = (typeof CHALLENGE_ABC_EXPERIMENT.posthogVariants)[number];

/** Every design the /challenges wrapper can paint, live or by preview. */
export const CHALLENGE_DESIGN_ARMS = [
  'control',
  'test',
  'form_first',
  'form_first_ad_match',
  'trimmed',
  'trimmed_headline',
] as const;
export type ChallengeDesignArm = (typeof CHALLENGE_DESIGN_ARMS)[number];

/**
 * A preview parameter or flag value as a known design, or null when it is not
 * one. null keeps the wrapper's existing "not assigned" handling.
 */
export const normalizeChallengeDesign = (
  value: string | boolean | null | undefined,
): ChallengeDesignArm | null => CHALLENGE_DESIGN_ARMS.find((arm) => arm === value) || null;

/** The design to render for a value. Anything unknown is the original page. */
export const resolveChallengeDesignArm = (
  value: string | boolean | null | undefined,
): ChallengeDesignArm => normalizeChallengeDesign(value) || 'control';

/** What one useABTest call reports for a flag. */
export type ChallengeFlagReading = {
  variant: string | boolean | null;
  isLoading: boolean;
  isExperimentActive: boolean;
  isPreviewOverride?: boolean;
};

type ChallengeExperimentConfig =
  | typeof CHALLENGE_ABC_EXPERIMENT
  | typeof CHALLENGE_CLARITY_HOME_EXPERIMENT;

export type ChallengeAssignmentDecision =
  | { status: 'resolving' }
  | { status: 'unassigned' }
  | {
      status: 'assigned';
      arm: ChallengeDesignArm;
      experiment: ChallengeExperimentConfig;
      isPreviewOverride: boolean;
    };

const servedArm = (
  reading: ChallengeFlagReading,
  arms: readonly ChallengeDesignArm[],
): ChallengeDesignArm | null =>
  reading.isExperimentActive ? arms.find((arm) => arm === reading.variant) || null : null;

/**
 * Which flag decides the arm (Rob, 2026-10-02). The A/B/C flag is read first;
 * if it serves one of its three arms, that arm wins. Otherwise Martin's flag
 * decides exactly as it does today. Neither serving an arm means unassigned,
 * which the page shows as the original (control) design.
 */
export const decideChallengeAssignment = (
  abc: ChallengeFlagReading,
  original: ChallengeFlagReading,
): ChallengeAssignmentDecision => {
  if (abc.isLoading) return { status: 'resolving' };
  const abcArm = servedArm(abc, CHALLENGE_ABC_EXPERIMENT.posthogVariants);
  if (abcArm) {
    return {
      status: 'assigned',
      arm: abcArm,
      experiment: CHALLENGE_ABC_EXPERIMENT,
      isPreviewOverride: Boolean(abc.isPreviewOverride),
    };
  }
  if (original.isLoading) return { status: 'resolving' };
  const originalArm = servedArm(original, CHALLENGE_CLARITY_HOME_EXPERIMENT.posthogVariants);
  if (originalArm) {
    return {
      status: 'assigned',
      arm: originalArm,
      experiment: CHALLENGE_CLARITY_HOME_EXPERIMENT,
      isPreviewOverride: Boolean(original.isPreviewOverride),
    };
  }
  return { status: 'unassigned' };
};

/**
 * Attribution every challenge Lead and PostHog event carries: the arm and the
 * flag that decided it, so the A/B/C test and Martin's test never mix.
 */
export type ChallengeExperimentContext = {
  experiment_name: string;
  experiment_variant: ChallengeDesignArm;
  experiment_flag: string;
  feature_flag: string;
  is_experiment_active: boolean;
  experiment_version?: string | number;
  [key: `$feature/${string}`]: ChallengeDesignArm;
};

/** Keep SDK automatic flag properties aligned with the design actually shown. */
export const getChallengeExperimentEventProperties = (context?: ChallengeExperimentContext) => ({
  [`$feature/${CHALLENGE_ABC_EXPERIMENT.featureFlag}`]: false,
  [`$feature/${CHALLENGE_CLARITY_HOME_EXPERIMENT.featureFlag}`]: false,
  ...context,
});

export const CHALLENGE_PAGE_EXPERIMENT = {
  name: 'challenge_page_control_refined_clarity_2026_08_31',
  featureFlag: 'challenge-page-control-refined-clarity-2026-08-31',
  posthogExperimentId: 459166,
  predecessorPosthogExperimentId: 445170,
  status: 'active',
  posthogVariants: CHALLENGE_PAGE_POSTHOG_VARIANTS,
  variants: {
    control: 'Current challenge page',
    test: 'Refined challenge page',
    clarity: 'Clarity-painted refined challenge page',
  },
  primaryMetric: 'Confirmed challenge registration conversion',
  secondaryMetrics: ['Challenge form submission conversion'],
} as const;
